import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, forwardRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import * as ActivityActions from '@curbnturf/activity/src/lib/+state/activity.actions';
import { ActivityLocationFacade } from '@curbnturf/activity/src/lib/+state/activity.facade';
import { CategorySvgComponent } from '@curbnturf/custom-svgs/src/lib/category-svg/category-svg.component';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import { SiteSvgsComponent } from '@curbnturf/custom-svgs/src/lib/site-svgs/site-svgs.component';
import { ActivityLocation, HelperFunctions, IActivityLocation } from '@curbnturf/entities';
import { formatActivityLocationFormValue, IActivityLocationForm, IPhotoForm } from '@curbnturf/form-helpers';
import { ImageUploadOptions } from '@curbnturf/image-processing';
import { ImageUploadComponent } from '@curbnturf/image-processing/src/lib/image-upload/image-upload.component';
import { SaveFavoriteComponent } from '@curbnturf/listing-edit';
import { DeleteConfirmationModalComponent } from '@curbnturf/ui/src/lib/delete-confirmation-modal/delete-confirmation-modal.component';
import { SpinnerComponent } from '@curbnturf/ui/src/lib/spinner/spinner.component';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { ActivityLocationPhotoComponent } from '../activity-location-display/activity-location-photo/activity-location-photo.component';
import { ActivityFormModalComponent } from '../add-activity-form/activity-form-modal/activity-form-modal.component';
import { AddActivityFormComponent } from '../add-activity-form/add-activity-form.component';
import { LocationMapComponent } from '../location-map/location-map.component';

@Component({
  standalone: true,
  selector: 'curbnturf-activity-photos',
  templateUrl: './activity-photos.component.html',
  styleUrls: ['./activity-photos.component.scss'],
  imports: [
    forwardRef(() => ActivityFormModalComponent),
    ActivityLocationPhotoComponent,
    forwardRef(() => AddActivityFormComponent),
    CategorySvgComponent,
    DeleteConfirmationModalComponent,
    ImageUploadComponent,
    IonicModule,
    forwardRef(() => LocationMapComponent),
    MiscSvgComponent,
    NgFor,
    NgIf,
    ReactiveFormsModule,
    forwardRef(() => SaveFavoriteComponent),
    SiteSvgsComponent,
    SpinnerComponent,
  ],
})
export class ActivityPhotosComponent implements OnChanges, OnDestroy {
  @Input() activityForm: FormGroup<IActivityLocationForm>;
  @Input() activity?: ActivityLocation;

  uploadOptions: ImageUploadOptions = {
    action: ActivityActions.uploadActivityImage,
    completeAction: ActivityActions.activityImagesUpdated,
    croppieOptions: {
      // viewport: { height: 280, width: 652 },
      // boundary: { height: 300, width: 698 },
    },
    output: {
      size: { height: 546, width: 1270 },
    },
    data: {},
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  files?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatchSave?: (files: any) => void;

  getPhotoUrl = HelperFunctions.getPhotoUrlFlatSmall;

  get photos(): FormArray<FormGroup<IPhotoForm>> | undefined {
    if (this.activityForm) {
      return this.activityForm.get('photos') as FormArray<FormGroup<IPhotoForm>>;
    }

    return;
  }

  get activityId(): number | undefined {
    if (this.activity && this.activity.id) {
      return this.activity.id;
    }

    if (this.activityForm) {
      const id = this.activityForm.get('id');
      if (id && id.value) {
        return id.value;
      }
    }

    return;
  }

  subscriptions: Subscription[] = [];

  constructor(
    private poiFacade: ActivityLocationFacade,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    this.updateActivityId();

    if (changes.activityForm && changes.activityForm.currentValue) {
      const idControl = this.activityForm.get('id');
      if (idControl) {
        if (this.files && this.dispatchSave) {
          this.dispatchSave(this.files);

          delete this.files;
          delete this.dispatchSave;
        }

        this.subscriptions.push(idControl.valueChanges.subscribe(() => this.updateActivityId()));
      }
    }

    if (changes.activity?.currentValue) {
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  removePhoto(index: number): void {
    if (this.activityId) {
      this.poiFacade.removePhoto(this.activityId, index);
      this.activityForm.markAsDirty();

      this.cdr.detectChanges();
    }
  }

  removePhotoFunction(index: number): () => void {
    return () => this.removePhoto(index);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  save(files?: any, dispatchSave?: (files: any) => void) {
    const activity = this.getActivityToSave();

    this.files = files;
    this.dispatchSave = dispatchSave;

    if (activity?.id) {
      this.poiFacade.update(activity);
    } else {
      this.poiFacade.create(activity);
    }

    this.activityForm.markAsPristine();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  activitySave(files: any, dispatchSave: (files: any) => void) {
    dispatchSave(files);
  }

  private getActivityToSave(): IActivityLocation {
    if (!this.activity) {
      this.activity = new ActivityLocation();
    }

    const value = formatActivityLocationFormValue(this.activityForm);

    return {
      ...this.activity,
      ...value,
    };
  }

  private updateActivityId() {
    this.uploadOptions.data = {
      activityId: this.activityId,
    };
  }
}
